<script setup lang="ts">
/**
 * App-shell half of "create it, then publish it here" (stores/publish-intent.ts): while an editor is open for a
 * player or group it shows where you'll come back to; once the new item is saved and you're back, it reopens
 * the Publish drawer with that item ticked.
 */
import { computed, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import PublishDrawer from './PublishDrawer.vue';
import { cancelPublishIntent, intentPublishedVersion, publishIntent, publishNow } from '../stores/publish-intent';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const creating = computed(() => !!publishIntent.value && !publishIntent.value.created && route.name === publishIntent.value.editor);
const back = computed(() => !!publishIntent.value?.created && route.fullPath === publishIntent.value.returnTo);
// Wandered off without saving: drop it.
watch(
  () => route.fullPath,
  () => {
    const i = publishIntent.value;
    if (i && !i.created && route.name !== i.editor && route.fullPath !== i.returnTo) cancelPublishIntent();
  },
);
function cancel(): void {
  const to = publishIntent.value?.returnTo;
  cancelPublishIntent();
  if (to) void router.push(to);
}
</script>

<template>
  <div
    v-if="creating"
    class="fixed inset-x-0 bottom-4 z-40 mx-auto flex w-fit max-w-[calc(100%-2rem)] items-center gap-3 rounded-full bg-slate-900 px-4 py-2 text-sm text-white shadow-lg dark:bg-slate-100 dark:text-slate-900"
    role="status"
    data-testid="publish-intent-banner"
  >
    <span class="min-w-0 truncate">{{ t('publish.intentBanner', { name: publishIntent!.target.name }) }}</span>
    <button type="button" class="shrink-0 font-medium underline" @click="cancel">{{ t('publish.cancel') }}</button>
  </div>
  <PublishDrawer
    v-if="publishIntent?.created"
    :open="back"
    :target="publishIntent.target"
    :preselect="[`${publishIntent.created.kind}:${publishIntent.created.id}`]"
    @close="cancelPublishIntent()"
    @published="intentPublishedVersion++"
  />
  <PublishDrawer :open="!!publishNow" :content="publishNow ?? []" @close="publishNow = null" @published="intentPublishedVersion++" />
</template>
